import type { ComputedRef, DeepReadonly } from 'vue';
import { inject, provide, type Ref } from 'vue';

export const SELECT_COLUMN_NAME = 'selectColumn';

export type SelectAllState = boolean;
export type SortState = {
  isDesc: boolean;
  currentColumn: string;
  sortableColumns: Set<string>;
};

export type Context = {
  columns: DeepReadonly<Ref<Set<string>>>;
  isLoading: DeepReadonly<Ref<boolean>>;
  isZebra: DeepReadonly<Ref<boolean>>;
  isSmall: DeepReadonly<Ref<boolean>>;
  isMultiSelectable: DeepReadonly<Ref<boolean>>;
  selectedAllState: DeepReadonly<ComputedRef<SelectAllState>>;
  selectedSomeState: DeepReadonly<ComputedRef<boolean>>;
  setIsMultiSelectable: (isMultiSelectable: boolean) => void;
  sortState: DeepReadonly<Ref<SortState>>;
  onToggleAll: (newValue?: boolean) => void;
  onToggleRow: (key: string, newValue?: boolean) => void;
  onIncludeSelectableRow: (key: string, value: boolean) => void;
  onIncludeColumn: (name: string, include: boolean) => void;
  onSort: (columnName: string, isDesc?: boolean) => void;
  isRowSelected: (key: string) => boolean;
  isRowSelectable: (key: string) => boolean;
  isTableEmpty: DeepReadonly<ComputedRef<boolean>>;
  isSelectable: DeepReadonly<ComputedRef<boolean>>;
};

const TABLE_CONTEXT_KEY = Symbol();
export const provideTableContext = (context: Context) => provide(TABLE_CONTEXT_KEY, context);
export const injectTableContext = () => {
  const context = inject<Context>(TABLE_CONTEXT_KEY);

  if (context === undefined) {
    throw new Error('TableContext was expected but received none');
  }

  return context;
};

export type RowContext = {
  index: DeepReadonly<Ref<number>>;
  key: DeepReadonly<Ref<string>>;
  measureElement: (node: Element | null) => void;
};

const ROW_CONTEXT_KEY = Symbol();
export const provideRowContext = (context: RowContext) => provide(ROW_CONTEXT_KEY, context);
export const injectRowContext = () => {
  const context = inject<RowContext>(ROW_CONTEXT_KEY);

  if (context === undefined) {
    throw new Error('RowContext was expected but received none');
  }

  return context;
};

export type SkeletonRowContext = {
  index: DeepReadonly<Ref<number>>;
};

const SKELETON_ROW_CONTEXT_KEY = Symbol();
export const provideSkeletonRowContext = (context: SkeletonRowContext) =>
  provide(SKELETON_ROW_CONTEXT_KEY, context);
export const injectSkeletonRowContext = () => {
  const context = inject<SkeletonRowContext>(SKELETON_ROW_CONTEXT_KEY);

  if (context === undefined) {
    throw new Error('SkeletonRowContext was expected but received none');
  }

  return context;
};

const SCROLL_WRAPPER_CONTEXT_KEY = Symbol();
export const provideScrollWrapperContext = () => provide(SCROLL_WRAPPER_CONTEXT_KEY, true);
export const isInsideScrollWrapperContext = () => inject(SCROLL_WRAPPER_CONTEXT_KEY, false);
